import { http } from './request';
import type {
  KnowledgeEntry,
  SemanticSearchRequest,
  SemanticSearchResult,
  CooperationRecord,
  CreateCooperationRecordRequest,
  PricingBenchmark,
  CreatePricingBenchmarkRequest,
  ProposalTemplate,
  ImportTask,
} from '../types/knowledge';
import type { PaginationParams } from '../types/api';

export const knowledgeApi = {
  // 知识库搜索
  semanticSearch: (data: SemanticSearchRequest) =>
    http.post<SemanticSearchResult>('/api/v1/knowledge/search', data),

  // 知识条目管理
  getEntries: (params?: PaginationParams & { type?: string; status?: string; query?: string; sort_by?: string; sort_order?: string }) =>
    http.getPaginated<KnowledgeEntry>('/api/v1/knowledge/entries', params),

  createEntry: (data: {
    type: string;
    title: string;
    content?: string;
    metadata?: Record<string, unknown>;
    tags?: string[];
    source?: string;
  }) =>
    http.post<KnowledgeEntry>('/api/v1/knowledge/entries', data),

  getEntry: (entryId: string) =>
    http.get<KnowledgeEntry>(`/api/v1/knowledge/entries/${entryId}`),

  updateEntry: (entryId: string, data: {
    title?: string;
    content?: string;
    metadata?: Record<string, unknown>;
    tags?: string[];
    status?: string;
  }) =>
    http.put<KnowledgeEntry>(`/api/v1/knowledge/entries/${entryId}`, data),

  deleteEntry: (entryId: string) =>
    http.delete<void>(`/api/v1/knowledge/entries/${entryId}`),

  // 合作记录
  getCooperationRecords: (params?: PaginationParams & { industry?: string; category?: string; platform?: string; country?: string }) =>
    http.getPaginated<CooperationRecord>('/api/v1/knowledge/cooperation-records', params),

  createCooperationRecord: (data: CreateCooperationRecordRequest) =>
    http.post<CooperationRecord>('/api/v1/knowledge/cooperation-records', data),

  getCooperationRecord: (recordId: string) =>
    http.get<CooperationRecord>(`/api/v1/knowledge/cooperation-records/${recordId}`),

  updateCooperationRecord: (recordId: string, data: Partial<CreateCooperationRecordRequest>) =>
    http.put<CooperationRecord>(`/api/v1/knowledge/cooperation-records/${recordId}`, data),

  deleteCooperationRecord: (recordId: string) =>
    http.delete<void>(`/api/v1/knowledge/cooperation-records/${recordId}`),

  // 报价基准
  getPricingBenchmarks: (params?: PaginationParams & { industry?: string; country?: string; platform?: string }) =>
    http.getPaginated<PricingBenchmark>('/api/v1/knowledge/pricing-benchmarks', params),

  createPricingBenchmark: (data: CreatePricingBenchmarkRequest) =>
    http.post<PricingBenchmark>('/api/v1/knowledge/pricing-benchmarks', data),

  getPricingBenchmark: (benchmarkId: string) =>
    http.get<PricingBenchmark>(`/api/v1/knowledge/pricing-benchmarks/${benchmarkId}`),

  updatePricingBenchmark: (benchmarkId: string, data: Partial<CreatePricingBenchmarkRequest>) =>
    http.put<PricingBenchmark>(`/api/v1/knowledge/pricing-benchmarks/${benchmarkId}`, data),

  deletePricingBenchmark: (benchmarkId: string) =>
    http.delete<void>(`/api/v1/knowledge/pricing-benchmarks/${benchmarkId}`),

  // 报价基准查询
  queryPricingBenchmarks: (params: {
    industry?: string;
    country?: string;
    platform?: string;
    follower_tier?: string;
    content_type?: string;
  }) =>
    http.get<PricingBenchmark[]>('/api/v1/knowledge/pricing-benchmarks/query', params),

  // 提案模板
  getProposalTemplates: (params?: { industry?: string; type?: string }) =>
    http.getPaginated<ProposalTemplate>('/api/v1/knowledge/proposal-templates', params),

  createProposalTemplate: (data: {
    name: string;
    description?: string;
    format: 'excel' | 'ppt';
    industry?: string;
    content_structure?: Record<string, unknown>;
  }) =>
    http.post<ProposalTemplate>('/api/v1/knowledge/proposal-templates', data),

  getProposalTemplate: (templateId: string) =>
    http.get<ProposalTemplate>(`/api/v1/knowledge/proposal-templates/${templateId}`),

  updateProposalTemplate: (templateId: string, data: {
    name?: string;
    description?: string;
    format?: 'excel' | 'ppt';
    industry?: string;
    content_structure?: Record<string, unknown>;
  }) =>
    http.put<ProposalTemplate>(`/api/v1/knowledge/proposal-templates/${templateId}`, data),

  deleteProposalTemplate: (templateId: string) =>
    http.delete<void>(`/api/v1/knowledge/proposal-templates/${templateId}`),

  // 导入导出
  importData: (data: { entries: Array<{
    type: string;
    title: string;
    content?: string;
    metadata?: Record<string, unknown>;
    tags?: string[];
    source?: string;
  }> }) =>
    http.post<ImportTask>('/api/v1/knowledge/import', data),

  exportData: (params?: { type?: string; format?: 'json' | 'csv' }) =>
    http.get<Blob>('/api/v1/knowledge/export', params, { responseType: 'blob' }),
};
